/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { type PlaylistController } from './PlaylistController'
import { noop, resolveValueProvider, type Unsubscribe } from '@amazon/vinyl-util'
import { createErrorHandler } from '@/errorHandler'
import { ctx } from '@/util/context'
import type { Player } from '@/playback/api'
import { createDisposer } from '@amazon/vinyl-util'
import { onPipClose, setHasVideo } from '@/video/videoStage'
import { reportCloudQueuePlayback } from '@/cloudQueue/cloudQueueService'

const { target, errorHandler } = ctx('playerPlaylistBinding')

export interface PlaylistPlayerBindingsDeps {
    readonly playerPromise: Promise<Player>
    readonly playlistController: PlaylistController
}

/**
 * Initializes bindings for PlayListController <--> VinylAmznPlayer
 */
export function initializePlaylistPlayerBindings(deps: PlaylistPlayerBindingsDeps): Unsubscribe {
    const disposer = createDisposer()
    const { add, dispose } = disposer

    const { playerPromise, playlistController } = deps

    // Native PiP X-button → unload the playlist. Without this, dismissing
    // the floating window would only exit PiP and the audio/video would
    // keep playing in the transport — the user's intent ("close this")
    // is clearly to stop, not to keep playing in the background.
    add(
        onPipClose(() => {
            playlistController.clear()
        })
    )

    /**
     * Anchor the cloud queue's gateway-side cursor so mutations
     * that need a "current entity" (setQueueShuffleMode,
     * setQueueLoopMode, …) succeed. Mirrors hornet's
     * CloudQueueStore.reportDevicePlayback{Playing,Paused,Ended}
     * lifecycle. Best-effort: any error is logged and swallowed —
     * a reporting blip shouldn't break playback.
     */
    function reportPlayback(state: 'PLAYING' | 'STOPPED' | 'ENDED', progressInMs?: number) {
        const queueId = playlistController.cloudQueueOptions.value?.queueId
        const entityRef = playlistController.current.value?.entityReferenceId
        if (!queueId || !entityRef) return
        const payload: Parameters<typeof reportCloudQueuePlayback>[0] = {
            queueId,
            entityReferenceId: entityRef,
            playbackState: state,
        }
        if (progressInMs != null) {
            ;(payload as { progressInMs?: number }).progressInMs = progressInMs
        }
        reportCloudQueuePlayback(payload).catch(noop)
    }

    playerPromise
        .then((player) => {
            if (disposer.disposed) return
            // Track which entry's load options are in flight so a
            // rapid track change doesn't have an older lazy resolution
            // win the race after a newer entry has been loaded.
            let loadingFor: unknown = null
            add(
                playlistController.current.onData((currentEntry) => {
                    setHasVideo(currentEntry?.video === true)
                    if (!currentEntry) {
                        loadingFor = null
                        player.unload()
                        return
                    }
                    loadingFor = currentEntry
                    resolveValueProvider(currentEntry.trackLoadOptions)
                        .then((options) => {
                            if (loadingFor !== currentEntry) return
                            player.load(options)
                            player.play().catch(errorHandler)
                            // Anchor the gateway as soon as we
                            // commit to playing this entry — even
                            // before audio actually starts, so the
                            // user can hit shuffle / repeat right
                            // away without the gateway throwing
                            // "Cannot get currentEntityReferenceId".
                            reportPlayback('PLAYING', 0)
                        })
                        .catch(errorHandler)
                })
            )
            // Keep the gateway in sync as the user pauses / resumes
            // or the player itself transitions in/out of playing.
            add(
                player.on('play', () => {
                    reportPlayback('PLAYING', player.currentTime * 1000)
                })
            )
            add(
                player.on('pause', () => {
                    reportPlayback('STOPPED', player.currentTime * 1000)
                })
            )
            // Prefetch the next entry's load options whenever the
            // controller's `next` observable resolves to a non-null
            // entry. Driven by the observable rather than a per-track
            // peek so a feed-side invalidation (e.g. party-queue
            // guest add) automatically retriggers the preload.
            add(
                playlistController.next.onData((nextEntry) => {
                    if (!nextEntry) return
                    const currentEntry = playlistController.current.value
                    resolveValueProvider(nextEntry.trackLoadOptions)
                        .then((options) => {
                            // Don't preload for an entry the user has
                            // since stepped past.
                            if (loadingFor !== currentEntry) return
                            player.preload(options)
                        })
                        .catch(errorHandler)
                })
            )
            add(
                player.on('ended', () => {
                    reportPlayback('ENDED', player.currentTime * 1000)
                    // Repeat-one short-circuits the auto-advance:
                    // seek the current entry back to 0 and resume.
                    // Repeat-all and off both fall through to the
                    // normal feed advance — when the feed has no
                    // further entries, the controller emits null
                    // (handled by the feed's own loopMode for
                    // cloud-queues that opted into LOOP_ALL on
                    // create).
                    if (playlistController.repeatMode.value === 'one') {
                        player.seekTo(0).catch(createErrorHandler(target))
                        player.play().catch(createErrorHandler(target))
                        return
                    }
                    playlistController.skipNext().catch(createErrorHandler(target))
                })
            )
        })
        .catch(errorHandler)
    return dispose
}
